import {
  RejectRequestModal,
  RequestAccessController,
  useRequestActions,
  useRequestPersistedStore,
  useRequestStore,
} from "entities/Request";
import { EditRequestBasicDetails } from "features/EditRequestBasicDetails";
import { useRequestLock } from "features/RequestLock";
import { useEffect, useState } from "react";
import { Accordion, Card, Container, Stack } from "react-bootstrap";
import { REQUEST_TYPES, ROLES } from "shared/config";
import { useUnsavedChangesPrompt } from "shared/hooks/useUnsavedChangesPrompt";
import { SuccessfulSubmitCard } from "shared/ui";
import { Button } from "ui";
import creditCardIcon from "../../../../assets/credit_card.svg";
import CardStatementTransaction from "../CardStatementTransaction/CardStatementTransaction";
import { useCostLinesStore } from "entities/CostLines";
import { RequestDetailsTopBar } from "widgets/RequestDetailsTopBar";
// import { RequestRejectionHistory } from "features/RequestRejectionHistory";
import { ReasonOfRejection } from "entities/Request";

// TODO Refactor, unification with RequestDetails

const access = new RequestAccessController();

function CardStatementDetails({
  cardFormat,
  basicDetailsDependencies,
  basicDetailsValidations,
}) {
  const request = useRequestStore((state) => state.request);
  const costLinesState = useCostLinesStore((state) => state.costLinesState);
  const viewRequestAs = useRequestPersistedStore(
    (state) => state.viewRequestAs
  );
  const [isModified, setIsModified] = useState(false);

  useEffect(() => {
    setIsModified(
      Object.values(costLinesState).some((state) => state.isModified)
    );
  }, [costLinesState]);

  const {
    triggerRequestSave,
    triggerRequestSubmit,
    triggerRequestReject,
    triggerRequestAccept,
    requestLinesRefs,
    requestBasicDetailsRef,
    loadingState,
  } = useRequestActions(request);

  useUnsavedChangesPrompt({
    showPrompt:
      isModified &&
      !loadingState.isSubmitted &&
      !loadingState.isAccepted &&
      [ROLES.USER, ROLES.LOCAL_ADMINISTRATOR, ROLES.ACCOUNTS_PAYABLE].includes(
        viewRequestAs.role
      ),
  });

  // TODO accordion logic decomposition

  const [activeAccordionItems, setActiveAccordionItems] = useState(
    request?.credit_cards_costs_transactions?.data?.map((_, idx) =>
      idx.toString()
    ) || []
  );
  const handleAccordionClick = (idx) => {
    if (!activeAccordionItems.includes(idx)) {
      setActiveAccordionItems((prev) => [...prev, idx]);
    } else {
      setActiveAccordionItems((prev) => prev.filter((key) => key !== idx));
    }
  };

  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const handleRejectionModalClose = () => setShowRejectionModal(false);
  const handleRejectionModalShow = () => setShowRejectionModal(true);

  useRequestLock(request?.id, {
    triggerRequest: viewRequestAs.role === ROLES.ACCOUNTS_PAYABLE,
  });

  const totalAmount = `${request?.credit_cards_costs_transactions.data?.reduce(
    (total, transaction) =>
      (total = +(total + transaction.total_amount).toFixed(2)),
    0
  )} ${request?.credit_cards_costs_transactions?.data[0]?.currency_code}`;

  if (loadingState.isSubmitted) {
    return <SuccessfulSubmitCard />;
  }

  return (
    <Container>
      {(request?.locks || request?.onHold) && <RequestDetailsTopBar />}
      <EditRequestBasicDetails
        ref={requestBasicDetailsRef}
        // @ts-ignore
        userRole={viewRequestAs.role}
        advancePaymentsQueue={null}
        // TODO Decomposition
        formHeader={
          <>
            <img
              src={creditCardIcon}
              style={{
                height: "40px",
                display: "inline",
              }}
              alt="Card expenses"
            />
            <h1 className="fs-3 mt-3">Card expenses {request.number}</h1>
          </>
        }
        representativeFullName={null}
        requestorFullName={request?.requestor}
        requestPlacedBy={request?.request_placed_by}
        validations={basicDetailsValidations}
        dependencies={basicDetailsDependencies}
        request={request}
        withAccomodation={false}
        withInternalOrders={
          request?.type !== REQUEST_TYPES.AMEX_BTA &&
          request?.type !== REQUEST_TYPES.CREDIT_CARD
        }
        isAdvancePaymentAllowed={false}
        totalAmount={totalAmount}
        disableInternalOrders={
          request?.type === REQUEST_TYPES.EUROCARD &&
          viewRequestAs.role !== ROLES.ACCOUNTS_PAYABLE
        }
      />
      {/* TODO Replace restored ReasonOfRejection component with RequestRejectionHistory after rejection_history is implemented in the card request response */}
      <ReasonOfRejection comment={request?.acceptations?.data?.comment} />
      {/* <RequestRejectionHistory
        rejections={request?.rejection_history}
        userRole={viewRequestAs.role}
      /> */}
      <div className="pb-5 d-flex justify-content-center">
        <Accordion activeKey={activeAccordionItems} className="flex-fill pb-5">
          {request?.credit_cards_costs_transactions?.data?.map(
            (transaction, idx) => {
              return (
                <Accordion.Item eventKey={idx.toString()} key={transaction.id}>
                  <Accordion.Header
                    onClick={() => handleAccordionClick(idx.toString())}
                  >
                    <span>{`#${idx + 1}`}</span>{" "}
                    <div className="d-flex w-100 gap-4 align-items-center me-4">
                      <small className="mb-0 ms-3 fw-bolder">
                        Transaction - {transaction.transaction_number}
                      </small>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Container className="text-start my-5" fluid>
                      <fieldset
                        disabled={access
                          .to("SubCosts", "edit")
                          .withHold()
                          .withLock()
                          .unprovided()}
                      >
                        <CardStatementTransaction
                          ref={requestLinesRefs[idx]}
                          // @ts-ignore
                          transaction={transaction}
                          requestIsEditable={access
                            .to("SubCosts", "edit")
                            .withHold()
                            .withLock()
                            .provided()}
                          cardFormat={cardFormat}
                        />
                      </fieldset>
                    </Container>
                  </Accordion.Body>
                </Accordion.Item>
              );
            }
          )}
        </Accordion>
      </div>
      {access
        .to("RequestControls", "view")
        .withHold()
        .withLock()
        .provided() && (
        <Card
          className="position-fixed start-50 translate-middle p-3"
          style={{ zIndex: "50", bottom: "1.5rem" }}
        >
          <RejectRequestModal
            show={showRejectionModal}
            handleClose={handleRejectionModalClose}
            rejectRequest={triggerRequestReject}
            isRejecting={loadingState.isRejecting}
          />
          <Stack gap={3} direction="horizontal">
            {[ROLES.USER, ROLES.LOCAL_ADMINISTRATOR].includes(
              viewRequestAs.role
            ) ? (
              <>
                <Button
                  onClick={triggerRequestSave}
                  isLoading={loadingState.isSaving}
                  disabled={loadingState.isSaving || loadingState.isSubmitting}
                  variant="outline-primary"
                >
                  Save
                </Button>
                <Button
                  onClick={triggerRequestSubmit}
                  isLoading={loadingState.isSubmitting}
                  disabled={loadingState.isSubmitting || loadingState.isSaving}
                >
                  Submit
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handleRejectionModalShow}
                  isLoading={loadingState.isRejecting}
                  disabled={loadingState.isRejecting}
                >
                  Reject request
                </Button>
                {viewRequestAs.role === ROLES.ACCOUNTS_PAYABLE && (
                  <Button
                    onClick={triggerRequestSave}
                    isLoading={loadingState.isSaving}
                    disabled={loadingState.isSaving}
                    variant="outline-primary"
                  >
                    Save
                  </Button>
                )}
                <Button
                  variant="outline-primary"
                  onClick={triggerRequestAccept}
                  isLoading={loadingState.isAccepting}
                  disabled={loadingState.isAccepting}
                >
                  Accept request
                </Button>
              </>
            )}
          </Stack>
        </Card>
      )}
    </Container>
  );
}

export default CardStatementDetails;
